import { SrPartialPackingTypeMap } from '@plato/data/starrocks/types';
import { Plus, RefreshCcw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { IncrementalQuantityField } from '../../../components/forms/incremental-quantity-field.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { GetCatalogProducts } from '../../../server/types/infer-trpc-types.ts';
import { checkQuantityForRounding } from '../utils/catalog-quantity-utils.ts';

type Product = GetCatalogProducts['mainProduct'];

type ProductRowProps = {
    isLoading: boolean;
    pageCount: number;
    product?: Product | null;
    onAddProduct: (quantity: number) => void;
};

type ProductDetails = {
    productVariantId: string;
    productVariantName: string;
    quantity: number;
    units: string;
    salesUnitsPerPackageUnit: number;
    partialPackingType: SrPartialPackingTypeMap;
};

export const AddProductFromCatalog: React.FC<ProductRowProps> = ({ isLoading, product, onAddProduct }) => {
    const { t } = useLocale();

    const [productDetails, setProductDetails] = useState<ProductDetails | null>(null);

    useEffect(() => {
        if (product && product.product_variant_id !== productDetails?.productVariantId) {
            setProductDetails({
                productVariantId: product.product_variant_id,
                productVariantName: product.product_variant_name,
                quantity: product.sales_units_per_package_unit || 1,
                units: product.quantity_unit_sales || '',
                salesUnitsPerPackageUnit: product.sales_units_per_package_unit || 1,
                partialPackingType: product.partial_packing_type!,
            });
        } else {
            setProductDetails(null);
        }
    }, [product]);

    const canEditNumber = !!productDetails && !isLoading;

    const canSubmit = useMemo(() => {
        if (isLoading) {
            return false;
        }
        if (!productDetails) {
            return false;
        }
        const { quantity, salesUnitsPerPackageUnit, partialPackingType } = productDetails;
        if (!quantity) {
            return false;
        }
        const checkedQuantity =
            checkQuantityForRounding(quantity, salesUnitsPerPackageUnit, partialPackingType) ?? 0;
        if (checkedQuantity !== quantity) {
            return false;
        }

        if (
            partialPackingType === SrPartialPackingTypeMap.NO_PACKAGE_BREAK &&
            quantity % salesUnitsPerPackageUnit !== 0
        ) {
            return false;
        }

        return true;
    }, [productDetails, isLoading]);

    const onSubmit = () => {
        if (!productDetails) {
            return false;
        }
        const { quantity, salesUnitsPerPackageUnit, partialPackingType } = productDetails;
        if (!quantity) {
            return;
        }
        const checkedQuantity =
            checkQuantityForRounding(quantity, salesUnitsPerPackageUnit, partialPackingType) ?? 0;
        setProductDetails({
            ...productDetails,
            quantity: checkedQuantity,
        });
        onAddProduct(checkedQuantity);
    };

    const onChangeValue = (value: number) => {
        if (!productDetails) {
            return;
        }
        setProductDetails({
            ...productDetails,
            quantity: value,
        });
    };

    return (
        <>
            <div className="flex w-full grow-0 flex-row items-center space-x-5 px-4 py-2.5 ">
                <div className="flex h-8 grow items-center rounded-lg border border-neutral-100 px-3 text-sm font-normal text-neutral-600">
                    {!isLoading &&
                        productDetails &&
                        `${productDetails.productVariantId} ${productDetails.productVariantName}`}

                    {!isLoading && !productDetails && t('no_product_found', { ns: 'catalog' })}

                    {isLoading && (
                        <>
                            <div className="flex flex-1" />
                            <div>
                                <RefreshCcw size={16} className="animate-spin" />
                            </div>
                        </>
                    )}
                </div>

                <IncrementalQuantityField
                    className="w-[200px]"
                    key={canEditNumber ? product?.product_variant_id : 'no-product'}
                    value={productDetails ? productDetails.quantity : null}
                    onValueChange={onChangeValue}
                    disabled={!canEditNumber}
                    partialPackingType={productDetails?.partialPackingType}
                    quantityUnit={productDetails?.units ?? ''}
                    salesUnitsPerPackageUnit={productDetails?.salesUnitsPerPackageUnit}
                />

                <div className="flex shrink-0">
                    <Button variant="primary" disabled={!canSubmit} onClick={onSubmit}>
                        <Plus className="mr-2" size={16} />
                        {t('add_product', { ns: 'catalog' })}
                    </Button>
                </div>
            </div>
        </>
    );
};
